import { useQuery } from '@tanstack/react-query'

import { httpClient } from '@/shared/utils/http-client'

// Lead type based on API response
export interface Lead {
  user_uuid: string
  email: string
  phone: string
  first_name: string
  last_name: string
  full_name: string
  hubspot_contact_id: string
}

// Building type based on API response
export interface Building {
  uuid: string
  hubspot_contact_id: string
  full_address: string
  address: string
  city: string
  postal_code: string
}

interface LeadsSearchResponse {
  data: Lead[]
  count: number
  limit: number
  offset: number
}

interface BuildingsSearchResponse {
  data: Building[]
  count: number
  limit: number
  offset: number
}

interface ApiWrappedResponse<T> {
  data: T
  success: boolean
}

// Use relative path in development to leverage Vite proxy
const API_BASE_URL = import.meta.env.DEV
  ? '/user/v1/admin'
  : 'https://scout-service-v2.enter.de/user/v1/admin'

class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

class LeadsApiClient {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`
      let responseBody = null

      try {
        responseBody = await response.json()
        errorMessage = responseBody.message || responseBody.error || errorMessage
        console.error('API Error Response:', responseBody)
      } catch {
        // If response body is not JSON, try to get text
        try {
          const textBody = await response.text()
          console.error('API Error Text:', textBody)
          errorMessage = textBody || errorMessage
        } catch {
          // If that fails too, use default error message
        }
      }

      throw new ApiError(errorMessage, response.status)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text()
      console.error('Non-JSON response:', text)
      const jsonError = new Error(`Expected JSON but got ${contentType}: ${text.substring(0, 200)}`)
      throw jsonError
    }

    return response.json() as Promise<T>
  }

  async searchLeads(params?: {
    q?: string
    limit?: number
    offset?: number
  }): Promise<LeadsSearchResponse> {
    const queryParams = new URLSearchParams()

    if (params?.q) {
      queryParams.append('q', params.q)
    }
    queryParams.append('limit', (params?.limit || 10).toString())
    queryParams.append('offset', (params?.offset || 0).toString())

    const url = `${API_BASE_URL}/leads${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    const wrappedResult =
      await this.handleResponse<ApiWrappedResponse<LeadsSearchResponse>>(response)

    // Extract the actual leads search response from the wrapped response
    return wrappedResult.data
  }

  async searchBuildings(
    leadUuid: string,
    params?: {
      limit?: number
      offset?: number
    }
  ): Promise<BuildingsSearchResponse> {
    const queryParams = new URLSearchParams()

    queryParams.append('limit', (params?.limit || 10).toString())
    queryParams.append('offset', (params?.offset || 0).toString())

    const url = `${API_BASE_URL}/leads/${leadUuid}/buildings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    const response = await httpClient.fetch(url, {
      method: 'GET',
    })

    const wrappedResult =
      await this.handleResponse<ApiWrappedResponse<BuildingsSearchResponse>>(response)

    // Extract the actual buildings search response from the wrapped response
    return wrappedResult.data
  }
}

export const leadsApi = new LeadsApiClient()

// React Query hooks
interface UseLeadsSearchParams {
  q?: string
  limit?: number
  offset?: number
  enabled?: boolean
}

interface UseBuildingsSearchParams {
  leadUuid?: string
  limit?: number
  offset?: number
  enabled?: boolean
}

export const useLeadsSearch = ({
  q,
  limit = 10,
  offset = 0,
  enabled = true,
}: UseLeadsSearchParams = {}) => {
  return useQuery({
    queryKey: ['leads', 'search', { q, limit, offset }],
    queryFn: () => leadsApi.searchLeads({ q, limit, offset }),
    enabled: enabled && !!q && q.length > 0,
    staleTime: 0, // No caching - always fetch fresh data
    gcTime: 0, // Don't keep data in cache after component unmounts
  })
}

export const useBuildingsSearch = ({
  leadUuid,
  limit = 10,
  offset = 0,
  enabled = true,
}: UseBuildingsSearchParams = {}) => {
  return useQuery({
    queryKey: ['buildings', 'search', { leadUuid, limit, offset }],
    queryFn: () => leadsApi.searchBuildings(leadUuid!, { limit, offset }),
    enabled: enabled && !!leadUuid,
    staleTime: 0, // No caching - always fetch fresh data
    gcTime: 0, // Don't keep data in cache after component unmounts
  })
}
